// useNotify.ts — колокольчик шапки (В1): подписка на живой канал + двери notifications.inbox.
// Вся логика состояния — в notifyStore.ts (без React, тестируется node:test), хук только
// подключает эффекты и переводит NotificationItem в NotifyItem кита. Текст/название источника —
// собственность продукта-источника (§15 спеки notifications): словарь догружается лениво
// productDict.ts, а не словарём Оболочки — свой словарь ключей продукта не знает.
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { sharedEventBus } from "../osnovaContext";
import { LIVE_FRAME_EVENT } from "../live";
import { fetchNotifications, dismissNotifications, markNotificationsSeen, answerAnnouncement } from "../api/notifications";
import { notifyText, productTitle, subscribeProductDict, getProductDictRevision } from "../i18n/productDict";
import { t } from "../i18n/i18n";
import { NotifyStore } from "./notifyStore";
import { openDeepLink, openNotifyItemEffect, toKitItem } from "./notify";
// active — продукт «Уведомления» установлен и жив (notifyStateFor); при false колокольчик
// остаётся тихим, без запросов (соседа может не быть вовсе — не только 503 в рантайме).
export function useNotify(active) {
    const store = useMemo(() => new NotifyStore({ fetchNotifications, dismissNotifications, markNotificationsSeen, answerAnnouncement }), []);
    const subscribe = useCallback((cb) => store.subscribe(cb), [store]);
    const getSnapshot = useCallback(() => store.getState(), [store]);
    const state = useSyncExternalStore(subscribe, getSnapshot);
    // Словарь продукта-источника приходит асинхронно (в фоне, после первого рендера строки) —
    // подписка на его ревизию перерисовывает items/toasts: ревизия в зависимостях useMemo ниже,
    // иначе при неизменной ссылке на state.items/state.toasts (тост не перезапрашивается, в отличие
    // от ящика, который перечитывает список при каждом открытии) текст остаётся фолбэком навсегда.
    const dictRevision = useSyncExternalStore(subscribeProductDict, getProductDictRevision);
    // drawerMount/drawerOpen — канон preview-platform.jsx (kit/v5): ящик монтируется по открытию,
    // onClose лишь запускает анимацию закрытия кита, снять с DOM — задача onClosed кита (F7).
    const [drawerMount, setDrawerMount] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    useEffect(() => {
        if (!active)
            return;
        store.load();
    }, [active, store]);
    useEffect(() => {
        if (!active)
            return;
        return sharedEventBus.on(`${LIVE_FRAME_EVENT}:notification`, (data) => store.onLiveFrame(data));
    }, [active, store]);
    // onOpenBell — при неактивном продукте «Уведомления» (F12) колокольчик не открывает ящик и не
    // зовёт двери; каждое открытие перечитывает список (F8), не только гасит счётчик.
    const onOpenBell = useCallback(() => {
        if (!active)
            return;
        if (drawerOpen) {
            setDrawerOpen(false);
            return;
        }
        setDrawerMount(true);
        setDrawerOpen(true);
        void store.load();
        void store.seen();
    }, [active, store, drawerOpen]);
    const onCloseDrawer = useCallback(() => setDrawerOpen(false), []);
    const onDrawerClosed = useCallback(() => setDrawerMount(false), []);
    // onOpenItem — поведение вынесено в openNotifyItemEffect (notify.ts): тестируется без React.
    const onOpenItem = useCallback((raw) => {
        openNotifyItemEffect(raw.target, {
            clear: () => void store.clear([raw.id]),
            closeDrawer: () => setDrawerOpen(false),
            navigate: openDeepLink,
        });
    }, [store]);
    const onClearItem = useCallback((raw) => { void store.clear([raw.id]); }, [store]);
    const onClearGroup = useCallback((raws) => { void store.clear(raws.map((r) => r.id)); }, [store]);
    const onReadAll = useCallback(() => { void store.clearAll(); }, [store]);
    const onDismissToast = useCallback((raw) => { store.dismissToast(raw.id); }, [store]);
    // onAnswerItem — annId приходит в самом item (toKitItem прокидывает его из NotificationItem.annId);
    // без него ответить некуда (объявление не v2 или params не донесли annId) — молча не зовём дверь.
    const onAnswerItem = useCallback((item, answerIds) => {
        if (!item.annId)
            return;
        void store.answer(item.id, item.annId, answerIds);
    }, [store]);
    const dict = useMemo(() => ({ notifyText, productTitle }), []);
    const yesNoFallback = useMemo(() => ({ yes: t("shell.notify.answer.yes"), no: t("shell.notify.answer.no") }), [dictRevision]);
    const items = useMemo(() => state.items.map((n) => toKitItem(n, dict, yesNoFallback)), [state.items, dictRevision, dict, yesNoFallback]);
    const toasts = useMemo(() => state.toasts.map((n) => toKitItem(n, dict, yesNoFallback)), [state.toasts, dictRevision, dict, yesNoFallback]);
    const bell = { count: state.count, open: drawerOpen, onClick: onOpenBell };
    return {
        bell, drawerOpen, drawerMount, items, toasts,
        onCloseDrawer, onDrawerClosed, onOpenItem, onClearItem, onClearGroup, onReadAll, onDismissToast, onAnswerItem,
    };
}
